import { Form, Select } from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { api, endpoint } from "@/api";

const ComparisonData = ({ form, formData = {}, setFormData }) => {
  // state
  const [businessList, setBusinessList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [parameterList, setParameterList] = useState([]);
  const [siteLocationList, setSiteLocationList] = useState([]);
  // loading
  const [isSiteLoading, setIsSiteLoading] = useState(false);
  const [isParameterLoading, setIsParameterLoading] = useState(false);
  const [isDeviceLoading, setIsDeviceLoading] = useState(false);

  // fetch
  const getBusiness = async () => {
    try {
      const data = await api.get(endpoint.business);
      setBusinessList(data?.data);
      if (data?.data?.length === 1) {
        const business = data?.data[0]?._id;
        const updatedDevices = [...(formData.devices || [])];
        updatedDevices[0] = { ...updatedDevices[0], business };
        form.setFieldsValue({ devices: updatedDevices });
        setFormData((pre) => ({ ...pre, devices: updatedDevices }));
        getSiteLocations(business);
      }
    } catch {
      setBusinessList([]);
      form.setFieldsValue({ devices: [{ business: undefined }] });
    }
  };

  const getSiteLocations = async (business) => {
    if (!business.length) return setSiteLocationList([]);
    setIsSiteLoading(true);
    try {
      const data = await api.get(`/site-location/my-site`, { params: { business, limit: 10000 } });
      if (data) {
        setSiteLocationList(data?.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)));
      } else {
        setSiteLocationList([]);
      }
    } catch {
      setSiteLocationList([]);
    } finally {
      setIsSiteLoading(false);
    }
  };

  // get device
  const getDevice = async (site) => {
    if (!site.length) return setDeviceList([]);
    setIsDeviceLoading(true);
    try {
      setDeviceList([]);
      const { results } = await api.get(endpoint.get_devices, {
        params: {
          site,
          limit: 100000,
          page: 1,
        },
      });

      if (results) {
        setDeviceList(results);
      }
    } catch {
      setDeviceList([]);
    } finally {
      setIsDeviceLoading(false);
    }
  };

  // get device parameter
  const getDeviceParameters = async (deviceId) => {
    if (!deviceId?.length) return setParameterList([]);
    setIsParameterLoading(true);
    try {
      setParameterList([]);
      const { results } = await api.get(endpoint.get_device_parameter, {
        params: {
          deviceId,
          limit: 1000,
          page: 1,
          sortBy: "name",
          orderBy: "ASC",
          type: "formula",
        },
      });
      setParameterList(results);
    } catch {
      setParameterList([]);
    } finally {
      setIsParameterLoading(false);
    }
  };

  // effects
  useEffect(() => {
    getBusiness();
  }, []);

  useEffect(() => {
    const businessField = form.getFieldValue(["devices", 0, "business"]);
    if (businessField && businessField.length > 0) {
      getSiteLocations(businessField);
    }
  }, [form]);

  useEffect(() => {
    const locationField = form.getFieldValue(["devices", 0, "site"]);
    if (locationField && locationField.length > 0) {
      getDevice(locationField);
    }
  }, [form]);

  useEffect(() => {
    const deviceField = form.getFieldValue(["devices", 0, "device"]);
    if (deviceField && deviceField.length > 0) {
      getDeviceParameters(deviceField);
    }
  }, [form]);

  return (
    <>
      <div>
        <Form.List name="devices" initialValue={formData?.devices || [{}]}>
          {(fields) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div key={key}>
                  <Form.Item
                    {...restField}
                    name={[name, "business"]}
                    label="Business"
                    rules={[{ required: true, message: "Select Business" }]}
                  >
                    <Select
                      style={{ width: "100%" }}
                      mode="multiple"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      maxTagCount="responsive"
                      placeholder="Select Business"
                      onChange={(values) => {
                        getSiteLocations(values);
                        // form.setFieldsValue({
                        //   devices: [{ business: values, site: undefined, device: undefined, parameters: undefined }],
                        // });
                      }}
                      disabled={businessList?.length === 1}
                      options={
                        Array.isArray(businessList)
                          ? businessList?.map((item) => ({ label: `${item.name} (${item.id})`, value: item._id }))
                          : []
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, "site"]}
                    label="Site Location"
                    rules={[{ required: true, message: "Select Site Location" }]}
                  >
                    <Select
                      style={{ width: "100%" }}
                      mode="multiple"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      maxTagCount="responsive"
                      placeholder="Select Site Location"
                      onChange={(values) => {
                        getDevice(values);
                        form.setFieldsValue({
                          devices: [
                            {
                              ...form.getFieldValue(["devices", 0]),
                              site: values,
                              device: undefined,
                              parameters: undefined,
                            },
                          ],
                        });
                      }}
                      loading={isSiteLoading}
                      options={
                        Array.isArray(siteLocationList)
                          ? siteLocationList?.map((item) => ({ label: item.name, value: item._id }))
                          : []
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, "device"]}
                    label="Device"
                    rules={[{ required: true, message: "Select Device" }]}
                  >
                    <Select
                      style={{ width: "100%" }}
                      mode="multiple"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      maxTagCount="responsive"
                      placeholder="Select Device"
                      loading={isDeviceLoading}
                      onChange={(values) => {
                        getDeviceParameters(values);
                        form.setFieldsValue({
                          devices: [
                            {
                              ...form.getFieldValue(["devices", 0]),
                              device: values,
                              parameters: undefined,
                            },
                          ],
                        });
                      }}
                      options={
                        Array.isArray(deviceList)
                          ? deviceList?.map((item) => ({ label: item.name, value: item._id, key: item._id }))
                          : []
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, "parameters"]}
                    label="Parameter"
                    rules={[{ required: true, message: "Select Parameter" }]}
                  >
                    <Select
                      style={{ width: "100%" }}
                      mode="multiple"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      maxTagCount="responsive"
                      placeholder="Select Parameter"
                      loading={isParameterLoading}
                      options={
                        Array.isArray(parameterList)
                          ? parameterList?.map((item) => ({
                              label: `${item?.device?.site?.name} > ${item?.device?.name} > ${item.name}`,
                              value: item._id,
                            }))
                          : []
                      }
                    />
                  </Form.Item>
                </div>
              ))}
            </>
          )}
        </Form.List>
      </div>
    </>
  );
};

export default ComparisonData;

ComparisonData.propTypes = {
  formData: PropTypes.object,
  form: PropTypes.object,
  setFormData: PropTypes.func.isRequired,
};
